/**
 * Feature flags for CommonChord
 * Controls gradual rollout of new features
 */

// Simple hash function for deterministic user assignment
function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

export interface FeatureFlag {
  enabled: boolean;
  percentage: number;
  description: string;
}

export interface FeatureFlagWithChecker extends FeatureFlag {
  checkUser: (userId: string) => boolean;
}

// Store percentages as constants to avoid circular references
const SERVER_PERSISTENCE_PERCENTAGE = 100; // Enable for all users

export const FEATURE_FLAGS: Record<string, FeatureFlag | FeatureFlagWithChecker> = {
  USE_SERVER_PERSISTENCE: {
    enabled: true,
    percentage: SERVER_PERSISTENCE_PERCENTAGE,
    description: 'Use server-side persistence instead of localStorage',
    checkUser: (userId: string) => {
      // Deterministic flag based on user ID
      const hash = hashCode(userId);
      return hash % 100 < SERVER_PERSISTENCE_PERCENTAGE;
    }
  } as FeatureFlagWithChecker,
  ENABLE_DATA_MIGRATION: {
    enabled: true,
    percentage: 100, // Everyone gets migration option
    description: 'Show data migration prompt for localStorage users'
  } as FeatureFlag
};

/**
 * Get current user ID for feature flag evaluation
 * For now, use a stable browser-specific ID
 */
export function getUserId(): string {
  let userId = localStorage.getItem('commonchord-user-id');
  if (!userId) {
    // Generate a stable ID for this browser
    userId = `browser-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('commonchord-user-id', userId);
  }
  return userId;
}

/**
 * Check if a feature is enabled for the current user
 */
export function isFeatureEnabled(featureName: keyof typeof FEATURE_FLAGS): boolean {
  const flag = FEATURE_FLAGS[featureName];
  
  if (!flag.enabled) {
    return false;
  }
  
  if (flag.percentage === 0) {
    return false;
  }
  
  if (flag.percentage === 100) {
    return true;
  }
  
  if ('checkUser' in flag && flag.checkUser) {
    return flag.checkUser(getUserId());
  }
  
  // Simple percentage check
  return Math.random() * 100 < flag.percentage;
}

/**
 * Override feature flags for testing
 * Only works in development mode
 */
export function overrideFeatureFlag(featureName: keyof typeof FEATURE_FLAGS, enabled: boolean) {
  if (import.meta.env.DEV) {
    const overrides = JSON.parse(localStorage.getItem('commonchord-feature-overrides') || '{}');
    overrides[featureName] = enabled;
    localStorage.setItem('commonchord-feature-overrides', JSON.stringify(overrides));
  }
}

/**
 * Get feature flag status with overrides
 */
export function getFeatureFlagStatus(featureName: keyof typeof FEATURE_FLAGS): boolean {
  if (import.meta.env.DEV) {
    const overrides = JSON.parse(localStorage.getItem('commonchord-feature-overrides') || '{}');
    if (featureName in overrides) {
      return overrides[featureName];
    }
  }
  
  return isFeatureEnabled(featureName);
}